import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL } from '../utils/apiConfig';
import { optimisticSave } from '../utils/optimisticSave';

export const useProductSale = (isOpen, preselectedProduct) => {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const { showError } = useToast();
  const { currentFiscalYear } = useFiscalYear();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantityInternal] = useState(0);

  const [existingSale, setExistingSale] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saleDate, setSaleDate] = useState('');

  // S6.B4 — dirty flag pour résoudre race double setSelectedProduct cascade.
  // Le hook fait 2 setSelectedProduct (1 sync via prop preselectedProduct
  // L68, 1 async via fetchProducts callback L33-34), chacun retrigger
  // fetchProductSales L70 → 2 GET /sales → 2 setQuantity(0) dans branche
  // "no existing sales" qui écrase une saisie utilisateur tapée
  // rapidement (Playwright ou user sur 3G). Le dirty flag empêche le
  // setter interne setQuantityInternal(0) d'écraser une saisie déjà
  // marquée dirty par l'utilisateur. Reset auto à la fermeture modal +
  // au chargement d'une vente existante (valeur "officielle" BE).
  // Item Monday 2940486101 — workaround spec-side S6.B3 conservé en
  // défense profondeur (triple garde-fou cumulé).
  const isQuantityDirtyRef = useRef(false);

  // Wrapper exposé : marque dirty quand l'utilisateur change la valeur
  // (consommé par <input onChange> dans ProductSaleModal:114).
  const setQuantity = useCallback((value) => {
    isQuantityDirtyRef.current = true;
    setQuantityInternal(value);
  }, []);

  // Reset dirty à la fermeture modal (isOpen → false)
  useEffect(() => {
    if (!isOpen) isQuantityDirtyRef.current = false;
  }, [isOpen]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const fiscalYearParam = currentFiscalYear?.id ? `?fiscal_year_id=${currentFiscalYear.id}` : '';
      const response = await axios.get(`${API_URL}/api/products${fiscalYearParam}`);
      const allProducts = response.data || [];
      setProducts(allProducts);
      if (preselectedProduct) {
        const found = allProducts.find(p => p.id === preselectedProduct.id);
        if (found) setSelectedProduct(found);
      }
    } catch (error) { logger.error('Failed to fetch products:', error); }
    finally { setLoading(false); }
  }, [preselectedProduct, currentFiscalYear?.id]);

  const fetchProductSales = useCallback(async (productId) => {
    try {
      const fiscalYearParam = currentFiscalYear?.id ? `?fiscal_year_id=${currentFiscalYear.id}` : '';
      const salesResponse = await axios.get(`${API_URL}/api/products/${productId}/sales${fiscalYearParam}`);
      const sales = salesResponse.data?.sales || [];

      if (sales.length > 0) {
        // Edition mode : valeur officielle BE chargée → reset dirty
        // (la saisie user éventuelle est remplacée par la valeur stockée).
        const sale = sales[0];
        setExistingSale(sale);
        setQuantityInternal(sale.quantity || 0);
        isQuantityDirtyRef.current = false;
        setIsEditMode(true);
        if (sale.date) setSaleDate(sale.date);
      } else {
        // S6.B4 : ne PAS écraser une saisie user dirty (race double
        // setSelectedProduct → 2 GET /sales tardifs).
        setExistingSale(null);
        if (!isQuantityDirtyRef.current) setQuantityInternal(0);
        setIsEditMode(false);
        if (currentFiscalYear?.start_date) setSaleDate(currentFiscalYear.start_date);
        else setSaleDate('');
      }
    } catch (error) {
      logger.error('Failed to fetch product sales:', error);
      setExistingSale(null);
      if (!isQuantityDirtyRef.current) setQuantityInternal(0);
      setIsEditMode(false);
    }
  }, [currentFiscalYear]);

  useEffect(() => { if (isOpen) fetchProducts(); }, [isOpen, fetchProducts]);
  useEffect(() => { if (preselectedProduct) setSelectedProduct(preselectedProduct); }, [preselectedProduct]);
  useEffect(() => {
    if (selectedProduct && currentFiscalYear) fetchProductSales(selectedProduct.id);
    else {
      setExistingSale(null);
      if (!isQuantityDirtyRef.current) setQuantityInternal(0);
      setIsEditMode(false);
    }
  }, [selectedProduct, currentFiscalYear, fetchProductSales]);

  const handleSubmit = async (onSaleRecorded, onClose) => {
    if (!selectedProduct || quantity <= 0) return;
    // Phase 1.1.D.4.b — fiscal_year_id is mandatory on create. The edit
    // path keeps its previous behaviour (handled in a later phase).
    if (!isEditMode && !currentFiscalYear?.id) {
      showError(t('products.alerts.selectFiscalYearFirst'));
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && existingSale?.sale_id) {
        const result = await optimisticSave(t, () =>
          axios.put(`${API_URL}/api/products/${selectedProduct.id}/sales/${existingSale.sale_id}`, {
            quantity: quantity,
            updated_at: existingSale?.updated_at,
          })
        );
        if (!result.ok) {
          onSaleRecorded?.();  // refresh data pour récupérer updated_at à jour
          return;
        }
      } else {
        await axios.post(`${API_URL}/api/products/${selectedProduct.id}/sales`, {
          product_id: selectedProduct.id,
          quantity: quantity,
          fiscal_year_id: currentFiscalYear.id,
          date: saleDate || undefined,
        });
      }
      onSaleRecorded?.();
      onClose?.();
    } catch (error) {
      logger.error('Failed to save sale:', error);
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (onSaleRecorded, onClose) => {
    if (!selectedProduct || !existingSale?.sale_id) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/products/${selectedProduct.id}/sales/${existingSale.sale_id}`);
      onSaleRecorded?.();
      onClose?.();
    } catch (error) { logger.error('Failed to delete sale:', error); }
    finally { setSubmitting(false); setShowDeleteConfirm(false); }
  };

  return {
    isDark, t, language, currentFiscalYear,
    products, loading, submitting,
    selectedProduct, setSelectedProduct,
    quantity, setQuantity,
    existingSale, isEditMode,
    showDeleteConfirm, setShowDeleteConfirm,
    saleDate, setSaleDate,
    handleSubmit, handleDelete, fetchProductSales,
  };
};
